import { Action, createReducer, on } from '@ngrx/store';
import { MfaActions } from '../actions';
import { FactorType, FactorStatusType, FactorVerificationStatusType, FactorVerificationStatus, Factor } from '@app/shared/models/mfa';
import { Region } from '@smash-sdk/core';

export const mfaFeatureKey = 'mfa';

export interface State {
  mfaRequired: boolean;
  defaultFactor: FactorType;
  factors: { [key in FactorType]: { status: FactorStatusType, verificationStatus: FactorVerificationStatusType, secret?: string } };
  token: string;

  verifyOtpLoading: boolean;
  verifyOtpSuccess: boolean;
  verifyOtpError: any;

  verifyApplicationFactorLoading: boolean;
  verifyApplicationFactorLoaded: boolean;
  verifyApplicationFactorError: any;

  verifyEmailFactorLoading: boolean;
  verifyEmailFactorLoaded: boolean;
  verifyEmailFactorError: any;

  sendOtpLoading: boolean;
  sendOtpLoaded: boolean;
  sendOtpError: any;

  createMfaApplicationFactorSecretLoading: boolean;
  createMfaApplicationFactorSecretLoaded: boolean;
  createMfaApplicationFactorSecretError: any;
  applicationSecret: string;

  region: Region,
  error: any;
}

export const initialState: State = {
  mfaRequired: false,
  defaultFactor: null,
  factors: null,
  token: null,

  verifyOtpLoading: false,
  verifyOtpSuccess: false,
  verifyOtpError: null,

  verifyApplicationFactorLoading: false,
  verifyApplicationFactorLoaded: false,
  verifyApplicationFactorError: null,

  verifyEmailFactorLoading: false,
  verifyEmailFactorLoaded: false,
  verifyEmailFactorError: null,

  sendOtpLoading: false,
  sendOtpLoaded: false,
  sendOtpError: null,

  createMfaApplicationFactorSecretLoading: false,
  createMfaApplicationFactorSecretLoaded: false,
  createMfaApplicationFactorSecretError: null,
  applicationSecret: null,

  region: null,
  error: null,
};

const inviteReducer = createReducer(
  initialState,
  // Reset Mfa
  on(MfaActions.ResetMfa, () => initialState),
  // Display Mfa Challenge
  on(MfaActions.DisplayMfaChallenge, (state, { token, region, mfa }) => ({
    ...state,
    mfaRequired: true,
    token,
    region,
    factors: mfa.factors,
    defaultFactor: mfa.defaultFactor,
  })),
  on(MfaActions.SendOtp, (state) => ({
    ...state,
    sendOtpLoading: true,
    sendOtpLoaded: false,
    sendOtpError: null,
  })),
  on(MfaActions.SendOtpSuccess, (state) => ({
    ...state,
    sendOtpLoading: false,
    sendOtpLoaded: true,
  })),
  on(MfaActions.SendOtpFailure, (state, { error }) => ({
    ...state,
    sendOtpLoading: false,
    sendOtpError: error,
  })),
  // Validate Otp
  on(MfaActions.VerifyOtp, (state) => ({
    ...state,
    verifyOtpLoading: true,
    verifyOtpSuccess: false,
    verifyOtpError: null,
  })),
  on(MfaActions.VerifyOtpSuccess, (state) => ({
    ...state,
    verifyOtpLoading: false,
    verifyOtpSuccess: true,
  })),
  on(MfaActions.VerifyOtpFailure, (state, { error }) => ({
    ...state,
    verifyOtpLoading: false,
    verifyOtpError: error,
  })),

  // Verify Application Factor
  on(MfaActions.VerifyApplicationFactor, (state) => ({
    ...state,
    verifyApplicationFactorLoading: true,
    verifyApplicationFactorLoaded: false,
    verifyApplicationFactorError: null,
  })),
  on(MfaActions.VerifyApplicationFactorSuccess, (state) => ({
    ...state,
    factors: {
      ...state.factors,
      [Factor.application]: {
        ...state.factors[Factor.application],
        verificationStatus: FactorVerificationStatus.verified,
      },
    },
    verifyApplicationFactorLoading: false,
    verifyApplicationFactorLoaded: true,
  })),
  on(MfaActions.VerifyApplicationFactorFailure, (state, { error }) => ({
    ...state,
    verifyApplicationFactorLoading: false,
    verifyApplicationFactorError: error,
  })),

  // Verify Email Factor
  on(MfaActions.VerifyEmailFactor, (state) => ({
    ...state,
    verifyEmailFactorLoading: true,
    verifyEmailFactorLoaded: false,
    verifyEmailFactorError: null,
  })),
  on(MfaActions.VerifyEmailFactorSuccess, (state) => ({
    ...state,
    verifyEmailFactorLoading: false,
    verifyEmailFactorLoaded: true,
  })),
  on(MfaActions.VerifyEmailFactorFailure, (state, { error }) => ({
    ...state,
    verifyEmailFactorLoading: false,
    verifyEmailFactorError: error,
  })),

  // Create Mfa Factor Secret
  on(MfaActions.CreateMfaFactorSecret, (state) => ({
    ...state,
    createMfaApplicationFactorSecretLoading: true,
    createMfaApplicationFactorSecretLoaded: false,
    createMfaApplicationFactorSecretError: null,
    secret: null,
  })),
  on(MfaActions.CreateMfaFactorSecretSuccess, (state, { secret }) => ({
    ...state,
    createMfaApplicationFactorSecretLoading: false,
    createMfaApplicationFactorSecretLoaded: true,
    applicationSecret: secret,
  })),
  on(MfaActions.CreateMfaFactorSecretFailure, (state, { error }) => ({
    ...state,
    createMfaApplicationFactorSecretLoading: false,
    createMfaApplicationFactorSecretError: error,
  })),
);

export function reducer(state: State | undefined, action: Action) {
  return inviteReducer(state, action);
}
